import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@shigo-ui/ui';

import type {
  DashboardDataTablePaginationProps,
  DashboardDataTablePaginationState,
} from './DashboardDataTable.types';

export function DashboardDataTableFooter({
  footerSummary,
  pagination,
  className,
}: {
  footerSummary: ReactNode | undefined;
  pagination: DashboardDataTablePaginationProps | undefined;
  className?: string | undefined;
}) {
  const safePagination = pagination
    ? { ...pagination, value: dashboardDataTableSafePaginationValue(pagination.value) }
    : undefined;
  const summary =
    footerSummary ?? (safePagination ? dashboardDataTableSummary(safePagination.value) : undefined);

  if (!summary && !safePagination) {
    return null;
  }

  return (
    <div
      data-dashboard-table-footer=""
      className={cn(
        'border-border grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 border-t px-4 py-3 md:flex md:justify-between',
        className,
      )}
    >
      {summary ? (
        <p className="text-muted-foreground text-[13px]">{summary}</p>
      ) : (
        <span aria-hidden="true" />
      )}
      {safePagination ? (
        <DashboardDataTablePagination {...safePagination} className="contents md:flex" />
      ) : null}
    </div>
  );
}

export function DashboardDataTablePagination({
  value,
  pageSizes,
  disabled = false,
  onChange,
  className,
}: DashboardDataTablePaginationProps & { className?: string }) {
  const totalPages = dashboardDataTableTotalPages(value);
  const lastPage = Math.max(totalPages - 1, 0);
  const safePage = dashboardDataTableSafePage(value.page, lastPage);
  const safeValue = { ...value, page: safePage };
  const { pageSize, totalItems } = safeValue;

  function handlePageSizeChange(nextSize: string) {
    onChange({ page: 0, pageSize: Number(nextSize), totalItems });
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <Select value={String(pageSize)} disabled={disabled} onValueChange={handlePageSizeChange}>
        <SelectTrigger className="h-8 w-28 text-[13px]" aria-label="Rows per page">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {pageSizes.map((size) => (
            <SelectItem key={size} value={String(size)}>
              {size} rows
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="col-span-2 flex items-center justify-end gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-8"
          aria-label="Previous"
          title="Previous page"
          disabled={disabled || safePage === 0}
          onClick={() => {
            onChange({ ...safeValue, page: Math.max(0, safePage - 1) });
          }}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </Button>
        <span className="text-muted-foreground min-w-24 text-center text-[13px]">
          Page {safePage + 1} of {Math.max(totalPages, 1)}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-8"
          aria-label="Next"
          title="Next page"
          disabled={disabled || safePage >= lastPage}
          onClick={() => {
            onChange({ ...safeValue, page: Math.min(lastPage, safePage + 1) });
          }}
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

function dashboardDataTableSafePaginationValue(
  value: DashboardDataTablePaginationState,
): DashboardDataTablePaginationState {
  return {
    ...value,
    page: dashboardDataTableSafePage(
      value.page,
      Math.max(dashboardDataTableTotalPages(value) - 1, 0),
    ),
  };
}

function dashboardDataTableSafePage(page: number, lastPage: number) {
  return Math.min(Math.max(0, Math.trunc(page)), lastPage);
}

function dashboardDataTableTotalPages(value: DashboardDataTablePaginationState) {
  return Math.max(1, Math.ceil(value.totalItems / value.pageSize));
}

function dashboardDataTableSummary({
  page,
  pageSize,
  totalItems,
}: DashboardDataTablePaginationState) {
  if (!totalItems) {
    return 'Showing 0-0 of 0';
  }

  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, totalItems);
  return `Showing ${String(from)}-${String(to)} of ${String(totalItems)}`;
}
